import { useState } from "react";
import { ShieldAlert, AlertTriangle, CheckCircle, XCircle, Loader2, Link as LinkIcon, Mail, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface PhishingIndicator {
  type: "high" | "medium" | "low";
  category: string;
  description: string;
  found: string;
}

interface PhishingResult {
  riskScore: number;
  riskLevel: "safe" | "suspicious" | "dangerous";
  indicators: PhishingIndicator[];
  recommendations: string[];
  senderAnalysis: {
    domain: string;
    isSuspicious: boolean;
    reason?: string;
  } | null;
}

export const PhishingDetector = () => {
  const [emailContent, setEmailContent] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<PhishingResult | null>(null);

  const sampleEmails = [
    {
      sender: "security@paypa1-support.com",
      content: `URGENT: Your PayPal account has been limited!

Dear Valued Customer,

We've noticed unusual activity on your account. Your account access has been limited until you verify your information.

Click here to verify immediately: http://paypal-verify.suspicious-site.com/login

If you don't verify within 24 hours, your account will be permanently suspended.

PayPal Security Team`
    },
    {
      sender: "newsletter@company.com",
      content: `Hi there,

Just wanted to follow up on our meeting last week about the Q1 planning. 

I've attached the proposal we discussed. Let me know if you have any questions.

Best regards,
John Smith
Marketing Manager`
    }
  ];

  const loadSample = () => {
    const sample = sampleEmails[Math.floor(Math.random() * sampleEmails.length)];
    setSenderEmail(sample.sender);
    setEmailContent(sample.content);
    setResult(null);
  };

  const analyzeEmail = () => {
    if (!emailContent.trim()) {
      toast.error("Please enter email content to analyze");
      return;
    }

    setIsAnalyzing(true);

    setTimeout(() => {
      const indicators: PhishingIndicator[] = [];
      const recommendations: string[] = [];
      const contentLower = emailContent.toLowerCase();
      const senderLower = senderEmail.toLowerCase();

      // Check sender domain
      let senderAnalysis = null;
      if (senderEmail) {
        const domain = senderEmail.split("@")[1] || "";
        const suspiciousDomainPatterns = [
          { pattern: /paypa[l1]/i, legit: "paypal.com" },
          { pattern: /amaz[o0]n/i, legit: "amazon.com" },
          { pattern: /g[o0]{2}gle/i, legit: "google.com" },
          { pattern: /micr[o0]s[o0]ft/i, legit: "microsoft.com" },
          { pattern: /app[l1]e/i, legit: "apple.com" },
        ];

        for (const check of suspiciousDomainPatterns) {
          if (check.pattern.test(domain) && !domain.includes(check.legit.split(".")[0])) {
            senderAnalysis = {
              domain,
              isSuspicious: true,
              reason: `Domain mimics ${check.legit} but is not the official domain`
            };
            indicators.push({
              type: "high",
              category: "Sender Spoofing",
              description: `Email appears to impersonate ${check.legit}`,
              found: senderEmail
            });
            break;
          }
        }

        if (!senderAnalysis) {
          senderAnalysis = { domain, isSuspicious: false };
        }
      }

      // Urgency indicators
      const urgencyPhrases = [
        "urgent", "immediately", "act now", "within 24 hours", "account suspended",
        "verify your account", "confirm your identity", "limited time", "expires soon"
      ];
      urgencyPhrases.forEach(phrase => {
        if (contentLower.includes(phrase)) {
          indicators.push({
            type: "medium",
            category: "Urgency Tactics",
            description: "Creates false sense of urgency to pressure quick action",
            found: phrase
          });
        }
      });

      // Suspicious links
      const linkPatterns = [
        /http:\/\/[^\s]+/gi,
        /https?:\/\/[0-9]+\.[0-9]+\.[0-9]+\.[0-9]+/gi,
        /bit\.ly|tinyurl|t\.co|goo\.gl/gi
      ];
      
      linkPatterns.forEach(pattern => {
        const matches = emailContent.match(pattern);
        if (matches) {
          matches.forEach(match => {
            indicators.push({
              type: "high",
              category: "Suspicious Links",
              description: "Contains potentially dangerous or obfuscated links",
              found: match
            });
          });
        }
      });

      // Credential requests
      const credentialPhrases = [
        "password", "credit card", "social security", "ssn", "bank account",
        "login credentials", "verify your information", "update your payment"
      ];
      credentialPhrases.forEach(phrase => {
        if (contentLower.includes(phrase)) {
          indicators.push({
            type: "high",
            category: "Credential Harvesting",
            description: "Requests sensitive personal or financial information",
            found: phrase
          });
        }
      });

      // Generic greetings
      const genericGreetings = ["dear customer", "dear user", "valued customer", "dear account holder"];
      genericGreetings.forEach(greeting => {
        if (contentLower.includes(greeting)) {
          indicators.push({
            type: "low",
            category: "Generic Greeting",
            description: "Uses impersonal greeting instead of your name",
            found: greeting
          });
        }
      });

      // Threat language
      const threatPhrases = ["suspended", "terminated", "closed", "blocked", "unauthorized access"];
      threatPhrases.forEach(phrase => {
        if (contentLower.includes(phrase)) {
          indicators.push({
            type: "medium",
            category: "Threat Language",
            description: "Uses threatening language about account status",
            found: phrase
          });
        }
      });

      // Calculate risk score
      let riskScore = 0;
      indicators.forEach(ind => {
        if (ind.type === "high") riskScore += 30;
        else if (ind.type === "medium") riskScore += 15;
        else riskScore += 5;
      });
      riskScore = Math.min(100, riskScore);

      // Determine risk level
      let riskLevel: "safe" | "suspicious" | "dangerous" = "safe";
      if (riskScore >= 60) riskLevel = "dangerous";
      else if (riskScore >= 30) riskLevel = "suspicious";

      // Generate recommendations
      if (riskLevel === "dangerous") {
        recommendations.push("Do NOT click any links in this email");
        recommendations.push("Do NOT reply or provide any personal information");
        recommendations.push("Report this email as phishing to your email provider");
        recommendations.push("If you're unsure, contact the company directly through their official website");
      } else if (riskLevel === "suspicious") {
        recommendations.push("Verify the sender by contacting the company through official channels");
        recommendations.push("Hover over links to check their actual destination before clicking");
        recommendations.push("Be cautious about providing any personal information");
      } else {
        recommendations.push("This email appears legitimate, but always verify unexpected requests");
        recommendations.push("Check that links go to official domains before clicking");
      }

      setResult({
        riskScore,
        riskLevel,
        indicators,
        recommendations,
        senderAnalysis
      });
      
      setIsAnalyzing(false);
    }, 1000);
  };

  const getRiskColor = (level: string) => {
    if (level === "safe") return "text-success";
    if (level === "suspicious") return "text-warning";
    return "text-destructive";
  };

  const getRiskBg = (level: string) => {
    if (level === "safe") return "bg-success/10 border-success/20";
    if (level === "suspicious") return "bg-warning/10 border-warning/20";
    return "bg-destructive/10 border-destructive/20";
  };

  const getRiskIcon = (level: string) => {
    if (level === "safe") return <CheckCircle className="w-6 h-6 text-success" />;
    if (level === "suspicious") return <AlertTriangle className="w-6 h-6 text-warning" />;
    return <XCircle className="w-6 h-6 text-destructive" />;
  };

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-destructive/10 flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-destructive" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Phishing Email Detector
          </h3>
          <p className="text-sm text-muted-foreground">
            Analyze emails for phishing indicators and scam patterns
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-foreground">
              Sender Email (optional)
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadSample}
              className="text-xs text-primary hover:text-primary/80"
            >
              Load Sample
            </Button>
          </div>
          <div className="relative">
            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={senderEmail}
              onChange={(e) => {
                setSenderEmail(e.target.value);
                setResult(null);
              }}
              placeholder="e.g., sender@domain.com"
              className="pl-10"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-foreground mb-2 block">
            Email Content
          </label>
          <Textarea
            value={emailContent}
            onChange={(e) => {
              setEmailContent(e.target.value);
              setResult(null);
            }}
            placeholder="Paste the suspicious email content here..."
            className="min-h-[150px] resize-none"
          />
        </div>

        <Button
          onClick={analyzeEmail}
          disabled={isAnalyzing || !emailContent.trim()}
          className="w-full"
          variant="destructive"
        >
          {isAnalyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning for threats...
            </>
          ) : (
            <>
              <ShieldAlert className="w-4 h-4 mr-2" />
              Detect Phishing
            </>
          )}
        </Button>

        {result && (
          <div className="space-y-4 pt-4 border-t border-border">
            {/* Risk Score */}
            <div className={`rounded-xl border p-4 ${getRiskBg(result.riskLevel)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getRiskIcon(result.riskLevel)}
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Phishing Risk Score
                    </p>
                    <p className={`text-2xl font-bold ${getRiskColor(result.riskLevel)}`}>
                      {result.riskScore}/100
                    </p>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-full text-sm font-bold uppercase ${getRiskColor(result.riskLevel)} bg-background/50`}>
                  {result.riskLevel}
                </div>
              </div>
            </div>

            {/* Sender Analysis */}
            {result.senderAnalysis && (
              <div className={`rounded-xl border p-4 ${result.senderAnalysis.isSuspicious ? "bg-destructive/5 border-destructive/20" : "bg-success/5 border-success/20"}`}>
                <div className="flex items-start gap-3">
                  <Mail className={`w-5 h-5 mt-0.5 ${result.senderAnalysis.isSuspicious ? "text-destructive" : "text-success"}`} />
                  <div>
                    <p className="text-sm font-medium text-foreground">Sender Domain: {result.senderAnalysis.domain}</p>
                    {result.senderAnalysis.isSuspicious ? (
                      <p className="text-xs text-destructive mt-1">{result.senderAnalysis.reason}</p>
                    ) : (
                      <p className="text-xs text-success mt-1">Domain appears legitimate</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Indicators Found */}
            {result.indicators.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  Red Flags Detected ({result.indicators.length})
                </h4>
                <div className="space-y-2">
                  {result.indicators.map((indicator, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        indicator.type === "high" 
                          ? "bg-destructive/5 border-destructive/20" 
                          : indicator.type === "medium"
                          ? "bg-warning/5 border-warning/20"
                          : "bg-muted/50 border-border"
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${
                              indicator.type === "high" 
                                ? "bg-destructive/20 text-destructive" 
                                : indicator.type === "medium"
                                ? "bg-warning/20 text-warning"
                                : "bg-muted text-muted-foreground"
                            }`}>
                              {indicator.type} risk
                            </span>
                            <span className="text-sm font-medium text-foreground">{indicator.category}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{indicator.description}</p>
                          <p className="text-xs text-foreground/70 mt-1 font-mono bg-muted/50 px-2 py-1 rounded inline-block">
                            Found: "{indicator.found}"
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className={`rounded-xl border p-4 ${
              result.riskLevel === "dangerous" 
                ? "bg-destructive/5 border-destructive/20" 
                : result.riskLevel === "suspicious"
                ? "bg-warning/5 border-warning/20"
                : "bg-success/5 border-success/20"
            }`}>
              <h4 className="text-sm font-medium text-foreground mb-2">What You Should Do</h4>
              <ul className="space-y-1.5">
                {result.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                    <span className={`mt-1 ${getRiskColor(result.riskLevel)}`}>•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
